// Real UMS-backed auth for the portal shell. Replaces the old mock session.ts.
//
// The browser only ever talks to the BFF (/api/portal/auth/*) — the UMS app
// key and the raw JWT stay server-side in an httpOnly cookie. These hooks wrap
// React Query so `me` is shared/cached across the shell (Sidebar, TopNav,
// route guard) without refetching.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type AuthUser = {
  username: string
  name: string
  title: string | null
  email: string | null
  realm: string | null
  roles: string[]
  isAdmin: boolean
  position: string | null
  orgPath: string | null
  exp: number | null
}

// Richer record fetched on demand from UMS GET /auth/users/{sub} (the Profile
// modal). Extends the fast JWT-derived AuthUser with fields the token omits.
export type UserProfile = AuthUser & {
  employeeId: string | null
  managerId: string | null
  positionLevel: string | null
  datasource: string | null
  emailVerified: boolean | null
  enabled: boolean | null
  createdAt: string | null
}

export type LoginCreds = { username: string; password: string }

export function useProfile(enabled: boolean) {
  return useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async (): Promise<UserProfile | null> => {
      const r = await fetch('/api/portal/auth/profile', { credentials: 'include' })
      if (r.status === 401) return null
      if (!r.ok) throw new Error('โหลดโปรไฟล์ไม่สำเร็จ')
      const j = (await r.json()) as { profile: UserProfile }
      return j.profile
    },
    enabled,
    staleTime: 300_000,
  })
}

async function fetchMe(): Promise<AuthUser | null> {
  const r = await fetch('/api/portal/auth/me', { credentials: 'include' })
  if (r.status === 401) return null
  if (!r.ok) throw new Error('ตรวจสอบสิทธิ์ไม่สำเร็จ')
  const j = (await r.json()) as { user: AuthUser }
  return j.user
}

// `null` = checked and not logged in; `undefined` while loading.
export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (creds: LoginCreds): Promise<AuthUser> => {
      const r = await fetch('/api/portal/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(creds),
      })
      const j = (await r.json().catch(() => ({}))) as { user?: AuthUser; error?: string }
      if (!r.ok || !j.user) throw new Error(j.error || 'เข้าสู่ระบบไม่สำเร็จ')
      return j.user
    },
    onSuccess: (user) => {
      qc.setQueryData(['auth', 'me'], user)
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await fetch('/api/portal/auth/logout', { method: 'POST', credentials: 'include' })
    },
    onSuccess: () => {
      qc.setQueryData(['auth', 'me'], null)
    },
  })
}
